import FaIcon from '@app/lui3/FaIcon';
import {useCallback} from 'react';
import styled from 'styled-components';

import {fetchOutlookJobAutocomplete, OutlookJobJson} from '../../requests';
import {usePodRequest} from '../../usePodRequest';
import EntityContainer from './EntityContainer';
import EntityLink from './EntityLink';
import LinkSelector from './LinkSelector';
import {JobLink, MessageAttachment} from './structs';

interface Props {
  agencyId: number;
  jobLinks: JobLink[];
  setJobLinks: (jobLinks: JobLink[]) => void;
  attachments: MessageAttachment[];
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const formatJobLabel = (job: OutlookJobJson): string => {
  const title = job.title ?? '';
  if (job.company_name) {
    return `${title} (${job.company_name})`;
  }
  return title;
};

const JobLinks = ({agencyId, jobLinks, setJobLinks, attachments}: Props) => {
  const {request} = usePodRequest();

  const linkedJobIds = new Set(jobLinks.map((link) => link.job.id));

  const searchJobs = useCallback(
    async (query: string) => {
      const {jobs} = await fetchOutlookJobAutocomplete(request, agencyId, {
        query,
      });
      return jobs;
    },
    [agencyId, request]
  );

  const handleSelect = (job: OutlookJobJson) => {
    if (linkedJobIds.has(job.id)) {
      return;
    }
    setJobLinks([...jobLinks, {job, pinned: false, attachmentIds: []}]);
  };

  const handleTogglePinned = (jobId: number) => {
    setJobLinks(
      jobLinks.map((link) =>
        link.job.id === jobId ? {...link, pinned: !link.pinned} : link
      )
    );
  };

  const handleRemove = (jobId: number) => {
    setJobLinks(jobLinks.filter((link) => link.job.id !== jobId));
  };

  const handleAttachmentsChanged = (jobId: number, attachmentIds: string[]) => {
    setJobLinks(
      jobLinks.map((link) =>
        link.job.id === jobId ? {...link, attachmentIds} : link
      )
    );
  };

  return (
    <Container>
      <LinkSelector
        title="Jobs"
        placeholder="Add Jobs..."
        searchOptions={searchJobs}
        getOptionKey={(job) => job.id}
        renderOption={formatJobLabel}
        onSelect={handleSelect}
        excludeKeys={linkedJobIds}
      />
      {jobLinks.length > 0 && (
        <EntityContainer>
          {jobLinks.map((link) => (
            <EntityLink
              avatar={
                <FaIcon icon="briefcase" faStyle="solid" color="gray-600" />
              }
              key={link.job.id}
              name={link.job.title ?? ''}
              subtitle={`#${link.job.id}, ${link.job.company_name ?? ''}`}
              attachmentIds={link.attachmentIds}
              allAttachments={attachments}
              showPinned
              pinned={link.pinned}
              attachmentType="Attachment"
              togglePinned={() => handleTogglePinned(link.job.id)}
              remove={() => handleRemove(link.job.id)}
              onAttachmentsChanged={(attachmentIds: string[]) =>
                handleAttachmentsChanged(link.job.id, attachmentIds)
              }
            />
          ))}
        </EntityContainer>
      )}
    </Container>
  );
};

export default JobLinks;
